"use client";
import { useState, useId, Fragment, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import CloseButton from "assets/svg/Icon_Small_Close.svg";
import ChevronDown from "assets/svg/chevron-down.svg";

import { urlMaker } from "services/url-maker";

import { selectedFilterElements } from "src/utils/filter-utils";
import { setMapOrListingFinalPath } from "src/helpers/map/commons";
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import Button from "components/common/button";
import Loading from "components/common/loading-spinner";

import styles from "./styles.module.css";

const SelectedFilterArea = ({ criteria, screenType, isProjectRadar }) => {
  const keyId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isShowMoreFilterIcon, setIsShowMoreFilterIcon] = useState(null);
  const filterValueRefs = useRef([]);
  const filterRefs = useRef([]);
  const wrapperRef = useRef();
  const innerWrapRef = useRef();


  const wrapperClasses = classNames({
    [styles.wrapper]: true,
    [styles.openWrapper]: isOpen,
    [styles.blurEffect]: loading,
    [styles.mapWrapper]: screenType === "map",
  });

  const dropdownIconClasses = classNames({
    [styles.dropdownIcon]: true,
    [styles.openDropdownIcon]: isOpen,
    [styles.loadingCloseButton]: loading,
  });

  const removeFilterIconClasses = classNames({
    [styles.icon]: true,
    [styles.isFilterDisable]: loading,
  });

  const updateLocations = (locations, filterKey, filter) => {
    switch (filterKey) {
      case "city":
        return null;
      case "districts":
        return {
          ...locations,
          districts: locations.districts?.filter((item) => item?.id !== filter?.id),
          towns: locations.towns?.filter((item) => item?.parent?.id !== filter?.id),
        };
      case "towns":
        return {
          ...locations,
          towns: locations.towns?.filter((item) => item?.id !== filter?.id),
          poiSet:
            locations.poiSet.length > 0
              ? locations.poiSet.map((poi) =>
                  poi?.poiList?.some((poiItem) => poiItem.parent.id === filter.id) ? {} : poi
                )
              : [],
        };
      default:
        return locations;
    }
  };

  const updateCriteriaParams = (params, filterKey, item, filter) => {
    if (filterKey === "company") {
      return params;
    }

    if (filterKey === "ad_type") {
      return params.filter((param) => param?.key !== filter?.key);
    }

    if (item?.value?.length === 1) {
      return params.filter((param) => param?.key !== item?.key);
    }

    const selectedFilterParams = params.find((param) => param?.key === filterKey);
    const newValue = selectedFilterParams?.value?.filter((val) => val?.id !== filter?.id);
    const newSelectedFilter = { ...selectedFilterParams, value: newValue };

    return params.filter((param) => param?.key !== selectedFilterParams?.key).concat(newSelectedFilter);
  };

  const handleRemoveFilter = async (item, filter) => {
    setLoading(true);
    const locationKeys = ["city", "districts", "towns"];
    const filterKey = item?.key;
    let locations = criteria?.location || {};
    let criteriaParams = criteria?.params || [];
    let company = criteria?.company || null;

    if (locationKeys.includes(filterKey)) {
      locations = updateLocations(locations, filterKey, filter);
    } else {
      criteriaParams = updateCriteriaParams(criteriaParams, filterKey, item, filter);
    }
    if (filterKey === "company") {
      company = {...criteria?.company, value: criteria?.company?.value?.filter((c) => c?.id !== String(filter?.id))};
    }
    // temporary solution until the endpoint here changes
    if (item.key === "para_birimi") {
      const copyCriteriaParams = JSON.parse(JSON.stringify(criteriaParams));
      const priceFilter = copyCriteriaParams.find((param) => param.key === "price");

      priceFilter.queryParams.filter((param) => !Object.prototype.hasOwnProperty.call(param, "para_birimi"));
      priceFilter.value.currency = "try";
      criteriaParams = copyCriteriaParams;
    }

    let newCriteria;
    if (item.key === "price" && isProjectRadar) {
      newCriteria = {
        ...criteria,
        location: locations,
        params: criteriaParams,
        radar: {
          key: "radar",
          value: true,
          maxPrice: null,
          minPrice: null,
        },
      };
    } else {
      newCriteria = { ...criteria, location: locations, params: criteriaParams, company };
    }

    const res = await urlMaker(newCriteria, "default");

    if (isProjectRadar) {
      window.location.href = `/proje-radari${res?.path}`;
    } else {
      window.location.href = setMapOrListingFinalPath({ res, screenType });
    }
  };

  const handleDeleteAllButton = async () => {
    Ga4WithEventParameters({
      eventName: "filtre",
      eventParameters: { label: "click||secimleri-kaldir" },
    });
    setLoading(true);
    const newCriteria = isProjectRadar
      ? {
          ...criteria,
          location: null,
          params: [],
          radar: {
            key: "radar",
            value: true,
            minPrice: null,
            maxPrice: null,
          },
        }
      : { ...criteria, location: null, params: [], company: null };

    const res = await urlMaker(newCriteria, "default");
    if (isProjectRadar) {
      window.location.href = "/proje-radari/";
    } else {
      window.location.href = setMapOrListingFinalPath({ res, screenType });
    }
  };

  const checkIfIconIsNeeded = () => {
    if (!wrapperRef.current || !innerWrapRef.current) return;

    const wrapperHeight = wrapperRef.current.clientHeight;
    const innerHeight = innerWrapRef.current.scrollHeight;

    setIsShowMoreFilterIcon(innerHeight > wrapperHeight);
  };

  useEffect(() => {
    setSelectedFilters(selectedFilterElements(criteria));
    if (isProjectRadar) {
      if (criteria?.radar?.maxPrice || criteria?.radar?.minPrice) {
        const allSelectedFilter = {
          ...criteria,
          params: [
            ...criteria?.params?.filter((filter) => filter?.key !== "owner"),
            {
              key: "price",
              name: "Fiyat",
              value: {
                max: criteria?.radar?.maxPrice,
                min: criteria?.radar?.minPrice,
              },
            },
          ],
        };
        setSelectedFilters(selectedFilterElements(allSelectedFilter));
      } else
        setSelectedFilters(
          selectedFilterElements({ ...criteria, params: criteria?.params?.filter((filter) => filter?.key !== "owner") })
        );
    }
    checkIfIconIsNeeded();
  }, [selectedFilters.length]);

  return (
    <>
      {selectedFilters?.length > 0 && (
        <div className={wrapperClasses} ref={wrapperRef}>
          <div className={styles.innerWrap} ref={innerWrapRef}>
            <span className={styles.title}>Seçilen Filtreler {`(${selectedFilters?.length})`}</span>
            <div className={styles.line}></div>
            {selectedFilters?.map((item, index) => (
              <Fragment key={`${keyId}-${index}`}>
                <span className={styles.info} ref={(el) => (filterRefs.current[index] = el)}>
                  {item.key === "owner" ? "Kimden" : item.name || item.key}
                </span>
                {item?.value?.map((filter, index) => (
                  <div
                    key={`${keyId}+${index}`}
                    className={styles.buttonWrapper}
                    ref={(el) => (filterValueRefs.current[index] = el)}
                  >
                    <Button disabled={loading} size="Auto" buttonClass={styles.buttonClass}>
                      <span className={styles.poiButtonText}>{filter.name}</span>
                      <CloseButton
                        className={removeFilterIconClasses}
                        onClick={() => handleRemoveFilter(item, filter)}
                      />
                    </Button>
                  </div>
                ))}
              </Fragment>
            ))}
            {selectedFilters?.length > 0 && (
              <button className={styles.deleteAllButton} onClick={handleDeleteAllButton}>
                Tümünü Temizle
              </button>
            )}
          </div>
          {isShowMoreFilterIcon && (
            <div onClick={() => setIsOpen(!isOpen)} className={dropdownIconClasses}>
              <ChevronDown />
            </div>
          )}
          {loading && (
            <div className={styles.loadingWrapper}>
              <Loading />
            </div>
          )}
        </div>
      )}
    </>
  );
};

SelectedFilterArea.propTypes = {
  criteria: PropTypes.object,
  screenType: PropTypes.string,
  isProjectRadar: PropTypes.bool,
};

export default SelectedFilterArea;
